import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const ShiftSwaps = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What a shift swap is</Typography>
      <Typography>
        When a staff member can no longer work an assigned shift, they can
        offer it as available for a colleague to take, instead of simply
        declining it. The shift is then advertised alongside normal open work,
        and the original person stays assigned — and stays responsible for the
        shift — until a colleague's application is accepted.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Offering a shift (staff)</Typography>
      <Typography>
        Open the shift from <DocLink url="/my-shifts" text="My Shifts" /> and
        choose <strong>Offer Shift as Available</strong>. The option only
        appears for confirmed shifts that haven't started yet. Choosing{' '}
        <strong>Cannot Attend</strong> on a shift that needs acknowledgement
        offers it out in the same way.
      </Typography>
      <Typography>
        The managers who own that kind of shift at its base are notified in
        their AmbuNet notification bell (the{' '}
        <strong>Shift cover needed</strong> notification) — Event Planning for
        event shifts, PTS co-ordinators for PTS, Personnel for rotas. Your own
        offer
        stays visible on Available Shifts as confirmation, marked{' '}
        <strong>Your swap offer</strong>, but you can't apply for it.
      </Typography>
      <Typography>
        While the offer is live, the shift shows a notice that it has been
        offered for reassignment, with a{' '}
        <strong>Cancel shift swap request</strong> button. Cancelling rejects
        any pending applications from colleagues and notifies them that the
        shift is no longer available.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Offering a shift — or saying you cannot attend — does not remove you
        from it. You remain assigned and responsible for it until a manager
        accepts another colleague's application or reassigns it.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Taking a swapped shift (staff)</Typography>
      <Typography>
        Offered shifts appear in{' '}
        <DocLink url="/available-shifts" text="Available Shifts" /> for staff
        with a matching role, labelled with who has offered the shift for a
        swap. Applying works exactly like applying for any other advertised
        shift.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reviewing swaps (managers)</Typography>
      <Typography>
        Active swap requests are listed at{' '}
        <DocLink url="/shift-swap-requests" text="Shift Swap Requests" /> —
        reachable from Global Roster and from home-screen team actions — so
        managers can see every shift currently offered for reassignment and
        whether anyone has applied.
      </Typography>
      <Typography>
        Applications for a swapped shift are reviewed through the normal{' '}
        <DocLink url="/shift-applications" text="Shift Applications" />{' '}
        process, or straight from the event's Shifts panel for event shifts.
        Accepting an application reassigns the shift to the new
        colleague, ends the swap, and notifies the original staff member that
        their shift has been reallocated.
      </Typography>
      <Alert severity="info" variant="outlined">
        Viewing swap requests and reviewing applications requires the{' '}
        <strong>Personnel</strong> or <strong>Event Planning</strong> team.
        Site administrators always have access.
      </Alert>
    </Stack>
  </Stack>
)

export default ShiftSwaps
